import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTopBar } from "../../../../context/TopBarContext";
import { usePlaylist } from "../../../../context/PlaylistContext";
import { useCreatives } from "../../../../context/CreativeContext";
import { ReactComponent as Filter } from "../../../../assets/icons/svg/filter-icon.svg";
import CustomButton from "../../../ui/CommonButton";
import { ReactComponent as Icon } from "../../../../assets/icons/svg/add-folder-whiteColor.svg";
import PlaylistCard from "../../../ui/Playlist_Card";
import { Search } from "lucide-react";

// Placeholder component for empty playlists
const PlaylistImagePlaceholder: React.FC = () => (
  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-[28px]">
    <svg
      className="w-16 h-16 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  </div>
);

const PlaylistListScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const { playlists, getPlaylistWithCreatives, getPlaylistWithImage } = usePlaylist();
  const { allCreatives } = useCreatives();
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const title = "Playlists";
    const showBack = false;

    setTitle(title);
    setShowBackButton(showBack);

    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });
  }, [location.pathname, setTitle, setShowBackButton, pushNavState]);

  const handleCreatePlaylist = () => {
    navigate("/playlists/create-playlist", { state: { autoOpenPopup: true } });
  };

  const handlePlaylistClick = (playlistId: string) => {
    const selectedPlaylist = playlists.find(
      (playlist) => playlist.id === playlistId
    );

    if (!selectedPlaylist) {
      console.error("Playlist not found:", playlistId);
      return;
    }

    console.log("Opening playlist:", selectedPlaylist);

    // Navigate to playlist detail view
    navigate(`/playlists/playlist/${playlistId}`, {
      state: {
        playlistDetails: selectedPlaylist,
        isNewPlaylist: false,
        fromPlaylistList: true,
        playlistStatus: selectedPlaylist.status,
        // FIXED: Pass selectedCreativeIds instead of selectedCreatives
        selectedCreativeIds: selectedPlaylist.creativeIds || [],
        // Pass the creative durations from playlist
        creativeDurations: selectedPlaylist.creativeDurations || {},
        // Don't show toast when opening existing playlist
        showAddedToast: false,
      },
    });
  };

  // CRITICAL FIX: Get playlists with proper dynamic images and content validation
  const playlistsWithImages = playlists.map(playlist => {
    console.log("=== PROCESSING PLAYLIST ===", playlist.title);
    console.log("Playlist creative IDs:", playlist.creativeIds);
    console.log("Creative count:", playlist.creativesCount);
    
    // Get playlist with dynamic image from first creative
    const playlistWithImage = getPlaylistWithImage(playlist.id, allCreatives || []);
    
    // CRITICAL FIX: Ensure creativesCount reflects actual creative IDs length
    const actualCreativesCount = playlist.creativeIds?.length || 0;
    const hasValidCreatives = actualCreativesCount > 0;
    
    console.log("Actual creatives count:", actualCreativesCount);
    console.log("Has valid creatives:", hasValidCreatives);
    console.log("Image URL:", playlistWithImage?.imageUrl);
    
    // Return playlist with corrected data
    const finalPlaylist = {
      ...(playlistWithImage || playlist),
      creativesCount: actualCreativesCount, // Use actual count
      // Only set imageUrl if we have creatives
      imageUrl: hasValidCreatives && playlistWithImage?.imageUrl ? playlistWithImage.imageUrl : undefined
    };
    
    console.log("Final playlist data:", finalPlaylist);
    return finalPlaylist;
  });

  const filteredPlaylists = playlistsWithImages.filter((playlist) =>
    playlist.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 bg-white min-h-screen mt-1">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold text-gray-900">
            Total {playlists.length} playlists
          </h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 3xl:w-6 3xl:h-6" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search playlists"
              className="w-64 pl-10 pr-4 3xl:pl-12 py-2.5 h-[32px] md:h-[32px] xl:h-[42px] lg:h-[46px] 2xl:h-[46px] 3xl:h-[52px] rounded-[8px] border border-gray-300 focus:outline-none focus:ring-blue-500 placeholder:text-sm placeholder:font-medium"
            />
          </div>

          {/* Filter Button */}
          <button
            className={`rounded-[10px] flex items-center justify-center relative
                        w-[32px] h-[32px] md:w-[32px] md:h-[32px]
                        xl:w-[42px] xl:h-[42px]
                        lg:w-[46px] lg:h-[46px]
                        2xl:w-[46px] 2xl:h-[46px]
                        3xl:w-[52px] 3xl:h-[52px] hover:bg-blue-50 transition-colors duration-200 text-blue-600 border-blue-500`}
            style={{ borderWidth: "0.5px" }}
          >
            <Filter className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>

          {/* Create Playlist Button */}
          <button
            onClick={handleCreatePlaylist}
            className="inline-flex items-center justify-center h-[32px] md:h-[32px] xl:h-[42px] lg:h-[46px] 2xl:h-[46px] 3xl:h-[52px] gap-2 bg-[#0091AF] hover:bg-[#007B8A] text-white px-4 py-2.5 rounded-[8px] font-xs"
          >
            <Icon className="w-5 h-5" />
            Create playlist
          </button>
        </div>
      </div>

      {/* Playlist Content */}
      {filteredPlaylists.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 5xl:grid-cols-6 gap-6">
          {filteredPlaylists.map((playlist) => (
            <div
              key={playlist.id}
              onClick={() => handlePlaylistClick(playlist.id)}
              className="cursor-pointer transform transition-transform hover:scale-105"
            >
              {/* CRITICAL FIX: Check both creativesCount AND imageUrl properly */}
              {playlist.creativesCount > 0 && playlist.imageUrl ? (
                <PlaylistCard
                  title={playlist.title}
                  creativesCount={playlist.creativesCount}
                  imageUrl={playlist.imageUrl}
                  status={playlist.status}
                  isScheduled={playlist.isScheduled}
                />
              ) : (
                // Empty playlist card with proper styling
                <div className="relative w-full aspect-[294/385]">
                  {/* Stack Effect - Bottom Card (Darkest) */}
                  <div
                    className="absolute bg-[#606164]"
                    style={{
                      width: "84.35%",
                      height: "100%",
                      top: "5.19%",
                      left: "7.82%",
                      borderRadius: "min(28.42px, 7.2vw)",
                      zIndex: 0,
                    }}
                  />
                  
                  {/* Stack Effect - Middle Card (Medium) */}
                  <div
                    className="absolute bg-[#46474A]"
                    style={{
                      width: "92.52%",
                      height: "100%",
                      top: "2.86%",
                      left: "3.74%",
                      borderRadius: "min(28.42px, 7.2vw)",
                      zIndex: 1,
                    }}
                  />
                  
                  {/* Main Card with Placeholder */}
                  <div
                    className="w-full h-[99.48%] relative overflow-hidden z-10 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
                    style={{
                      borderRadius: "min(28px, 7.1vw)",
                    }}
                  >
                    {/* Empty State Icon */}
                    <svg
                      className="w-16 h-16 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    
                    {/* Gradient Overlay for Text */}
                    <div className="absolute inset-0 rounded-[28px] bg-gradient-to-t from-[rgba(27,29,33,0.7)] via-transparent to-transparent" />
                    
                    {/* Card Content */}
                    <div className="absolute bottom-4 left-4 right-4 text-white z-10">
                      {/* Playlist Title */}
                      <h3 className="text-base 2xl:text-lg font-semibold leading-[1.5] mb-1">
                        {playlist.title}
                      </h3>
                      
                      {/* Metadata Row */}
                      <div className="text-xs 2xl:text-sm flex items-center gap-1">
                        {/* Creatives Count */}
                        <span>No content</span>
                        
                        {/* Separator */}
                        <span className="mx-1">|</span>
                        
                        {/* Status Display */}
                        <span className="flex items-center font-medium text-[#FF9500] italic">
                          Draft Saved
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // Empty State
        <div className="flex flex-col items-center justify-center py-20">
          <div className="text-center">
            <svg
              className="w-24 h-24 text-gray-300 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? "No playlists found" : "No playlists yet"}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm
                ? `No playlists match "${searchTerm}"`
                : "Create your first playlist to get started"}
            </p>
            {!searchTerm && (
              <CustomButton
                variant="default"
                onClick={handleCreatePlaylist}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0BA6C7] hover:bg-[#0A99B9] text-white font-medium rounded-lg transition-colors"
              >
                <Icon className="w-5 h-5" />
                Create your first playlist
              </CustomButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistListScreen;